import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Tooltip } from '../types';
import { PlayCircle, Download, FileText, FileSpreadsheet, FileCode, File, X } from 'lucide-react';

interface VideoLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  tooltip: Tooltip | null;
}

function getYoutubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}?autoplay=1`;
  }
  if (url.includes('youtube.com/embed/')) {
    return url.includes('?') ? `${url}&autoplay=1` : `${url}?autoplay=1`;
  }
  return null;
}

function getPandaEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  // Se for uma tag iframe completa, extrai a URL do atributo src
  if (url.includes('<iframe')) {
    const match = url.match(/src="([^"]+)"/);
    if (match) {
      const src = match[1];
      return src.includes('autoplay=') ? src : src.includes('?') ? `${src}&autoplay=true` : `${src}?autoplay=true`;
    }
  }
  // Caso contrário, assume que é a URL limpa do embed
  return url.includes('autoplay=') ? url : url.includes('?') ? `${url}&autoplay=true` : `${url}?autoplay=true`;
}

export const VideoLessonModal: React.FC<VideoLessonModalProps> = ({ isOpen, onClose, tooltip }) => {
  const { theme } = useTheme();

  if (!isOpen || !tooltip) return null;

  const { videoPlataforma, videoUrl, videoTitulo, videoDescricao, videoAnexoUrl, videoAnexoNome, titulo } = tooltip;

  let embedUrl: string | null = null;
  if (videoPlataforma === 'youtube') {
    embedUrl = getYoutubeEmbedUrl(videoUrl);
  } else if (videoPlataforma === 'pandavideo') {
    embedUrl = getPandaEmbedUrl(videoUrl);
  }

  // Helper para renderizar o ícone correspondente à extensão do anexo
  const renderAttachmentIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'csv':
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'webp':
        return <FileCode className="w-8 h-8 text-blue-500" />;
      default:
        return <File className="w-8 h-8 text-gray-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div 
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl transition-colors border"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color)',
          color: 'var(--text-primary)'
        }}
      >
        {/* Header do Modal */}
        <div 
          className="flex items-center justify-between p-5 border-b sticky top-0 z-10"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)'
          }}
        >
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <PlayCircle className="w-5 h-5 text-blue-500" />
            {videoTitulo || `Saiba mais sobre ${titulo}`}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo do Modal */}
        <div className="p-6 space-y-6">
          {/* Iframe Player */}
          {embedUrl ? (
            <div className="w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 relative">
              <iframe
                src={embedUrl}
                title={videoTitulo || `Videoaula ${titulo}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="w-full aspect-video bg-gray-100 dark:bg-gray-900 rounded-xl flex flex-col items-center justify-center text-gray-400 gap-2 border">
              <PlayCircle className="w-12 h-12 text-gray-300 dark:text-gray-700" />
              <p className="text-sm font-medium">Link de vídeo indisponível ou inválido</p>
            </div>
          )}

          {/* Descrição da Videoaula */}
          {videoDescricao && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Descrição da aula
              </h4>
              <p 
                className="text-sm font-normal leading-relaxed whitespace-pre-line"
                style={{ color: 'var(--text-secondary)' }}
              >
                {videoDescricao}
              </p>
            </div>
          )}

          {/* Arquivos Anexos */}
          {videoAnexoUrl && (
            <div 
              className="p-4 rounded-xl border flex items-center justify-between gap-4"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-color)'
              }}
            >
              <div className="flex items-center gap-3">
                {renderAttachmentIcon(videoAnexoNome || '')}
                <div>
                  <p className="text-sm font-semibold leading-tight">
                    Material Complementar
                  </p>
                  <p 
                    className="text-xs font-normal"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {videoAnexoNome || 'arquivo_anexo'}
                  </p>
                </div>
              </div>
              <a
                href={videoAnexoUrl}
                download={videoAnexoNome || 'anexo'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 decoration-none"
              >
                <Download className="w-4 h-4" />
                Baixar Material
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
